import React, { memo, useState } from 'react';
import { Handle, Position, useReactFlow } from '@xyflow/react';
import { Button } from '@/components/ui/button';
import { 
  MapPin, 
  Focus,
  Calendar,
  Trash2,
  Camera,
  Volume2,
  Zap
} from 'lucide-react';
import { ZoneScheduleDialog } from '../ZoneScheduleDialog';

interface ZoneNodeData {
  zoneId: string;
  zoneName: string;
  isDefault: boolean;
  zone?: any; // MonitoringZoneDto
  companyId?: string;
  siteId?: string;
  onDeviceDrop?: (deviceData: any, zoneId: string) => void;
  onRemoveEverything?: (zoneId: string) => void;
  totalCameraCount?: number;
}

interface ZoneNodeProps {
  id: string;
  data: ZoneNodeData;
  selected?: boolean;
}

export const ZoneNode: React.FC<ZoneNodeProps> = memo(({ id, data, selected }) => {
  const { fitView, getNode } = useReactFlow();
  const [isDragOver, setIsDragOver] = useState(false);
  const [dragOverData, setDragOverData] = useState<any>(null);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);

  const handleFitToZone = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    const node = getNode(id);
    if (node) {
      // Fit view to this specific zone with nice padding
      fitView({
        nodes: [node],
        padding: 0.3,
        duration: 800,
        maxZoom: 1.2,
        minZoom: 0.1,
      });
    }
  };

  const handleEditSchedule = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowScheduleDialog(true);
  };

  const handleRemoveEverything = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.onRemoveEverything) {
      data.onRemoveEverything(data.zoneId);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Stop React Flow from handling this
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
    
    
    // Try to parse drag data for display
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('application/json'));
      setDragOverData(dragData);
    } catch (error) {
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Stop React Flow from handling this
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    setDragOverData(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Stop React Flow from handling this
    setIsDragOver(false);
    setDragOverData(null);


    try {
      let dragDataStr = e.dataTransfer.getData('application/json');
      if (!dragDataStr) {
        dragDataStr = e.dataTransfer.getData('text/plain');
      }
      
      const dragData = JSON.parse(dragDataStr);
      
      if (data.onDeviceDrop && (dragData.deviceType === 'speaker' || dragData.deviceType === 'relay' || dragData.deviceType === 'camera' || dragData.deviceType === 'multi-camera')) {
        data.onDeviceDrop(dragData, data.zoneId);
      } 
    } catch (error) {
    }
  };

  const getZoneIcon = () => {
    return <MapPin className="h-4 w-4" style={{ color: '#d1f551' }} />;
  };

  const getZoneColor = () => {
    const zoneBackgroundStyle = { backgroundColor: '#17221f' };
    if (data.isDefault) {
      return {
        border: '',
        contentBorder: 'border-dashed border-gray-600',
        bg: '',
        bgStyle: zoneBackgroundStyle,
        header: 'bg-[#1a201f]',
      };
    }
    return {
      border: '', 
      contentBorder: 'border-dashed border-gray-600',
      bg: '',
      bgStyle: zoneBackgroundStyle,
      header: 'bg-[#1a201f]',
    };
  };

  const colors = getZoneColor();

  return (
    <div 
      className={`w-full h-full ${colors.border} ${colors.bg} rounded-lg shadow-lg transition-all duration-200 flex flex-col ${
        selected ? 'ring-2 ring-blue-400 ring-opacity-75' : ''
      } ${
        isDragOver ? 'border-green-400 border-solid bg-green-900/20 shadow-green-400/20' : ''
      }`}
      style={{ minWidth: '300px', minHeight: '200px', ...colors.bgStyle }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Zone Header */}
      <div className={`${colors.header} px-3 py-2 rounded-t-md border-b border flex items-center justify-between`}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {getZoneIcon()}
          <span className="text-sm font-semibold text-white truncate">
            {data.zoneName}
          </span>
          {(data.totalCameraCount || 0) > 20 && (
            <span className="text-xs text-yellow-400 ml-2 truncate">
              (Previews hidden due to more than 20 cameras - hover to view)
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          {/* Remove Everything Button */}
          <Button
            size="xs"
            variant="ghost"
            className="h-6 w-6 p-0 flex-shrink-0 hover:bg-red-600/20"
            onClick={handleRemoveEverything}
            title="Remove everything from zone"
          >
            <Trash2 className="h-3 w-3 text-red-400" />
          </Button>
          
          {/* Edit Schedule Button - only show for non-default zones */}
          {!data.isDefault && data.zone && data.companyId && data.siteId && (
            <Button
              size="xs"
              variant="ghost"
              className="h-6 w-6 p-0 flex-shrink-0"
              onClick={handleEditSchedule}
              title="Edit Schedule"
            >
              <Calendar className="h-3 w-3" style={{ color: '#d1f551' }} />
            </Button>
          )}
          
          <Button
            size="xs"
            variant="ghost"
            className="h-6 w-6 p-0 flex-shrink-0"
            onClick={handleFitToZone}
            title="Focus on this zone"
          >
            <Focus className="h-3 w-3" style={{ color: '#d1f551' }} />
          </Button>
        </div>
      </div>

      {/* Zone Content Area */}
      <div className={`p-4 ${colors.contentBorder} border-2 rounded-b-lg flex-1`}>
        <div className="h-full flex items-center justify-center pt-12">
          {isDragOver && dragOverData?.deviceType === 'multi-camera' ? (
            <div className="text-center">
              <div className="text-sm text-green-400 font-medium">
                Drop {dragOverData.count} cameras
              </div>
              <div className="text-xs text-gray-400">
                to enable monitoring
              </div>
            </div>
          ) : (
            <div className="text-xs text-gray-500">
              Drag devices here to monitor
            </div>
          )}
        </div>
      </div>

      {/* React Flow Handles for potential connections */}
      <Handle
        type="target"
        position={Position.Top}
        className="opacity-0"
        isConnectable={false}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="opacity-0"
        isConnectable={false}
      />

      {/* Schedule Dialog */}
      {!data.isDefault && data.zone && data.companyId && data.siteId && (
        <ZoneScheduleDialog
          open={showScheduleDialog}
          onOpenChange={setShowScheduleDialog}
          zone={data.zone}
          projectId={data.siteId}
          companyId={data.companyId}
        />
      )}
    </div>
  );
});

ZoneNode.displayName = 'ZoneNode';